import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import teacherService from '../../services/api/teacher';
import { 
  BarChart3,
  ArrowRight,
  Download,
  TrendingUp,
  Trophy,
  Users,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Filter,
  ChevronLeft,
  ShieldCheck,
  Zap,
  Target,
  Sparkles,
  PieChart
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';

const QuizResults = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState('all');

  // جلب تفاصيل الاختبار
  const { data: quizData, isLoading: quizLoading } = useQuery(
    ['quiz-details', quizId],
    () => teacherService.getQuizDetails(quizId).then(res => res.quiz)
  );

  // جلب نتائج الاختبار
  const { data: resultsData, isLoading: resultsLoading } = useQuery(
    ['quiz-results', quizId],
    () => teacherService.getQuizAttempts(quizId),
    { retry: 1 }
  );

  const quiz = quizData || {};
  const attempts = resultsData?.attempts || [];

  // الحسابات الإحصائية (المنطق الأصلي كاملاً)
  const stats = {
    totalAttempts: attempts.length,
    passedAttempts: attempts.filter(a => a.passed).length,
    averageScore: attempts.length > 0 
      ? (attempts.reduce((sum, a) => sum + parseFloat(a.percentage || 0), 0) / attempts.length).toFixed(1)
      : 0,
    highestScore: attempts.length > 0 
      ? Math.max(...attempts.map(a => parseFloat(a.percentage || 0)))
      : 0,
    lowestScore: attempts.length > 0 
      ? Math.min(...attempts.map(a => parseFloat(a.percentage || 0)))
      : 0
  };

  // تصفية النتائج
  const filteredAttempts = attempts.filter(attempt => {
    if (filterStatus === 'passed') return attempt.passed;
    if (filterStatus === 'failed') return !attempt.passed;
    return true;
  });

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('ar-EG', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
  };

  // تنسيق الألوان بناءً على النسبة (المنطق الأصلي مطور)
  const getScoreBadge = (percentage) => {
    const p = parseFloat(percentage);
    if (p >= 85) return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (p >= 65) return 'bg-blue-50 text-blue-700 border-blue-100';
    if (p >= 50) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-rose-50 text-rose-700 border-rose-100';
  };

  // تحويل الثواني إلى صيغة MM:SS (المنطق الأصلي كاملاً)
  const formatTime = (seconds) => {
    const totalSeconds = Math.round(seconds || 0);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (quizLoading || resultsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحليل نتائج الطلاب...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-8 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
              <div className="bg-purple-100 p-2 rounded-2xl shadow-lg text-purple-600">
                <BarChart3 size={28} />
              </div>
              تحليل نتائج الاختبار
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                <ShieldCheck size={18} className="text-emerald-500" />
                الاختبار: <span className="text-gray-900 font-bold">{quiz.title}</span>
            </p>
          </div>
          <button
            onClick={() => navigate('/teacher/quizzes')}
            className="group bg-gray-50 text-gray-500 px-6 py-3 rounded-2xl font-black text-sm hover:bg-white hover:text-primary-600 hover:shadow-md transition-all flex items-center gap-2 border border-gray-100"
          >
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" /> العودة للاختبارات
          </button>
        </div>
      </div>

      {/* Analytics KPI Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
        {[
          { label: 'إجمالي المحاولات', value: stats.totalAttempts, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'طلاب ناجحون', value: stats.passedAttempts, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50', sub: `(${((stats.passedAttempts / stats.totalAttempts) * 100 || 0).toFixed(0)}%)` },
          { label: 'متوسط الدرجات', value: `${stats.averageScore}%`, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'أعلى درجة', value: `${stats.highestScore}%`, icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-50' },
          { label: 'أدنى درجة', value: `${stats.lowestScore}%`, icon: AlertCircle, color: 'text-rose-600', bg: 'bg-rose-50' }
        ].map((card, i) => (
          <div key={i} className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm flex flex-col items-center text-center group hover:shadow-md transition-all">
            <div className={`p-4 ${card.bg} ${card.color} rounded-2xl mb-4 group-hover:scale-110 transition-transform shadow-inner`}>
              <card.icon size={24} />
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none mb-2">{card.label}</p>
            <div className="flex items-baseline gap-1">
                <p className="text-2xl font-black text-gray-900 leading-none">{card.value}</p>
                {card.sub && <span className="text-[10px] font-bold text-gray-400">{card.sub}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Search, Filter & Export Toolbar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-6 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4 w-full md:w-auto">
          <div className="p-2 bg-gray-50 rounded-xl text-gray-400"><Filter size={20} /></div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="flex-1 md:w-64 px-6 py-3 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white transition-all font-black text-xs text-gray-700 appearance-none cursor-pointer"
          >
            <option value="all">عرض كافة النتائج ({attempts.length})</option>
            <option value="passed">الطلاب الناجحون ({attempts.filter(a => a.passed).length})</option>
            <option value="failed">الطلاب الراسبون ({attempts.filter(a => !a.passed).length})</option>
          </select>
        </div>

        <button className="w-full md:w-auto flex items-center justify-center gap-2 bg-emerald-600 text-white px-10 py-4 rounded-2xl font-black text-sm shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all transform active:scale-95">
          <Download size={18} /> تصدير تقرير Excel
        </button>
      </div>

      {/* Main Results Table */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">
        {filteredAttempts.length === 0 ? (
          <div className="py-24 text-center">
            <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <BarChart3 size={48} className="text-gray-200" />
            </div>
            <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">لا توجد محاولات</h3>
            <p className="text-gray-500 max-w-sm mx-auto font-medium">
              {attempts.length === 0 
                ? 'لم يقم أي طالب بمحاولة حل هذا الاختبار بعد.' 
                : 'لا توجد نتائج تطابق خيارات الفلترة المحددة.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-right border-collapse">
              <thead className="bg-gray-50/50">
                <tr className="border-b border-gray-100">
                  <th className="px-8 py-6 text-[10px] font-black text-gray-400 uppercase tracking-widest">الطالب المستجيب</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">الدرجة الفعلية</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">النسبة المئوية</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">النتيجة</th>
                  <th className="px-8 py-6 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">الوقت المستغرق</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">تاريخ المحاولة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredAttempts.map((attempt) => (
                  <tr key={attempt.attempt_id} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-xl flex items-center justify-center font-black shadow-sm group-hover/row:bg-primary-600 group-hover/row:text-white transition-all">
                            {attempt.student_name?.charAt(0).toUpperCase() || 'S'}
                        </div>
                        <div>
                          <p className="text-base font-black text-gray-900 group-hover/row:text-primary-600 transition-colors">{attempt.student_name || 'طالب مجهول'}</p>
                          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter flex items-center gap-1">
                              <Mail size={10} /> {attempt.email || 'no-email@platform.com'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      <div className="inline-flex flex-col items-center">
                          <span className="text-base font-black text-gray-900">
                            {attempt.score || 0}
                          </span>
                          <span className="text-[8px] font-black text-gray-400 uppercase">من أصل {quiz.total_questions || 0}</span>
                      </div>
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      <span className={`inline-flex items-center justify-center px-4 py-1.5 rounded-xl font-black text-xs border ${getScoreBadge(attempt.percentage)}`}>
                        {attempt.percentage || 0}%
                      </span>
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      {attempt.passed ? (
                        <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-emerald-50 text-emerald-700 font-black text-[10px] uppercase tracking-widest border border-emerald-100 shadow-sm">
                          <CheckCircle2 size={12} /> ناجح
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl bg-rose-50 text-rose-700 font-black text-[10px] uppercase tracking-widest border border-rose-100 shadow-sm">
                          <XCircle size={12} /> لم يوفق
                        </span>
                      )}
                    </td>
                    <td className="px-8 py-6 text-center whitespace-nowrap">
                      <div className="inline-flex items-center gap-1.5 text-gray-700 font-black text-sm bg-gray-50 px-3 py-1 rounded-lg border border-gray-100">
                        <Clock size={14} className="text-primary-500" />
                        {formatTime(attempt.time_spent_minutes)}
                      </div>
                    </td>
                    <td className="px-8 py-6 text-left whitespace-nowrap">
                        <div className="flex flex-col items-start lg:items-end">
                            <span className="text-sm font-black text-gray-800">{formatDate(attempt.completed_at)}</span>
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">توقيت التسليم</span>
                        </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grade Distribution Insights Section */}
      <div className="bg-white rounded-[2.5rem] p-8 lg:p-10 border border-gray-100 shadow-sm relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-2 h-full bg-primary-600 opacity-20 group-hover:opacity-100 transition-opacity"></div>
        <div className="flex items-center justify-between mb-10">
            <h2 className="text-2xl font-black text-gray-900 flex items-center gap-3 tracking-tighter uppercase">
              <PieChart size={24} className="text-primary-600" />
              توزيع مستويات الطلاب
            </h2>
            <div className="bg-primary-50 text-primary-600 px-4 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-widest border border-primary-100">
                Grade Analysis
            </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-emerald-50/50 rounded-3xl p-6 border border-emerald-100 group/level hover:bg-emerald-50 transition-all">
            <div className="flex items-center justify-between mb-4">
                <span className="bg-white p-2 rounded-xl text-emerald-600 shadow-sm"><Zap size={18} fill="currentColor" /></span>
                <span className="text-2xl font-black text-emerald-700">{attempts.filter(a => parseFloat(a.percentage || 0) >= 85).length}</span>
            </div>
            <p className="text-sm font-black text-emerald-900 mb-1">ممتاز (85-100%)</p>
            <p className="text-[10px] font-bold text-emerald-600/60 uppercase">مستوى التميز الأكاديمي</p>
          </div>

          <div className="bg-blue-50/50 rounded-3xl p-6 border border-blue-100 group/level hover:bg-blue-50 transition-all">
            <div className="flex items-center justify-between mb-4">
                <span className="bg-white p-2 rounded-xl text-blue-600 shadow-sm"><Target size={18} fill="currentColor" /></span>
                <span className="text-2xl font-black text-blue-700">
                    {attempts.filter(a => { const p = parseFloat(a.percentage || 0); return p >= 65 && p < 85; }).length}
                </span>
            </div>
            <p className="text-sm font-black text-blue-900 mb-1">جيد جداً (65-84%)</p>
            <p className="text-[10px] font-bold text-blue-600/60 uppercase">مستوى استيعاب مرتفع</p>
          </div>

          <div className="bg-amber-50/50 rounded-3xl p-6 border border-amber-100 group/level hover:bg-amber-50 transition-all">
            <div className="flex items-center justify-between mb-4">
                <span className="bg-white p-2 rounded-xl text-amber-500 shadow-sm"><Activity size={18} /></span>
                <span className="text-2xl font-black text-amber-700">
                    {attempts.filter(a => { const p = parseFloat(a.percentage || 0); return p >= 50 && p < 65; }).length}
                </span>
            </div>
            <p className="text-sm font-black text-amber-900 mb-1">مقبول (50-64%)</p>
            <p className="text-[10px] font-bold text-amber-600/60 uppercase">بانتظار التحسن</p>
          </div>

          <div className="bg-rose-50/50 rounded-3xl p-6 border border-rose-100 group/level hover:bg-rose-50 transition-all">
            <div className="flex items-center justify-between mb-4">
                <span className="bg-white p-2 rounded-xl text-rose-500 shadow-sm"><AlertCircle size={18} /></span>
                <span className="text-2xl font-black text-rose-700">{attempts.filter(a => parseFloat(a.percentage || 0) < 50).length}</span>
            </div>
            <p className="text-sm font-black text-rose-900 mb-1">ضعيف (&lt;50%)</p>
            <p className="text-[10px] font-bold text-rose-600/60 uppercase">يحتاج لدعم تعليمي</p>
          </div>
        </div>
      </div>

      {/* Floating Action Hint */}
      <div className="bg-gray-900 rounded-[3rem] p-10 text-white flex flex-col md:flex-row items-center justify-between gap-8 relative overflow-hidden group shadow-2xl">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/20 rounded-full blur-[100px] -z-0 translate-x-1/2 -translate-y-1/2"></div>
        <div className="relative z-10 text-center md:text-right space-y-2">
            <h3 className="text-2xl font-black flex items-center gap-2 justify-center md:justify-start leading-tight">
                <Sparkles className="text-primary-500" /> نصيحة لتطوير الأداء
            </h3>
            <p className="text-gray-400 font-medium max-w-xl leading-relaxed">بإمكانك مراسلة الطلاب ذوي المستوى "الضعيف" مباشرة من قائمة الطلاب لتشجيعهم أو تقديم شرح إضافي لرفع معدل نجاح الاختبار.</p>
        </div>
        <button 
            onClick={() => navigate('/teacher/students')}
            className="relative z-10 bg-white text-gray-900 px-10 py-4 rounded-2xl font-black flex items-center gap-2 shadow-xl hover:bg-primary-50 transition-colors transform active:scale-95 whitespace-nowrap"
          >
            قائمة الطلاب <ChevronLeft size={20} />
        </button>
      </div>

    </div>
  );
};

// أيقونات إضافية
const Mail = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" />
    </svg>
);

const Activity = ({ size, className }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className={className}>
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
);

export default QuizResults;